import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || 'programming');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const webPageSchema = {
    '@type': 'CollectionPage',
    name: `${category.name} - 草榴社区技术讨论区`,
    description: category.description,
    url: `https://techcommunity.cn/category/${category.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-primary-600 text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {category.description} · 共 {categoryArticles.length} 篇文章
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex gap-8">
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-3">技术分类</h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap flex items-center gap-2.5 ${
                          activeCategory === cat.slug
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                        {cat.name}
                        <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="lg:hidden mb-6">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`px-3.5 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                          activeCategory === cat.slug
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {categoryArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-article-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无文章</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="block bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-all duration-200 group"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {article.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                                  {tag}
                                </span>
                              ))}
                              <span className="text-xs text-foreground-500">{article.date}</span>
                              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                            </div>
                            <h2 className="text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors mb-2">
                              {article.title}
                            </h2>
                            <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                              {article.excerpt}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-200/70">
                          <div className="flex items-center gap-2">
                            <img src={article.authorAvatar} alt={article.author} className="w-6 h-6 rounded-full object-cover" title={article.author} />
                            <span className="text-xs text-foreground-600">{article.author}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-foreground-500 ml-auto">
                            <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views}</span>
                            <span className="flex items-center gap-1"><i className="ri-heart-line"></i> {article.likes}</span>
                            <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i> {article.comments}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default CategoryPage;